import { TdHTMLAttributes } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';

import { wacToken } from '@/libraries/design/foundations/index.ts';

import { useGenericTableVariant } from './generic-table-variant-context';

// Only layout/border CSS — content styling is the responsibility of slot children
const cellCss = css({
	backgroundColor: 'inherit',
	display: 'table-cell',
	verticalAlign: 'top',
	padding: '14px',
	borderInlineEnd: `1px solid ${wacToken('color.neutral.light400')}`,
	':last-of-type': {
		borderInlineEnd: 'none',
	},
	[media.below.sm]: {
		borderInlineStart: 0,
		maxWidth: 'var(--generic-table-mobile-column-width)',
		minWidth: 'var(--generic-table-mobile-column-width)',
		width: 'var(--generic-table-mobile-column-width)',
		':first-of-type': {
			borderInlineEnd: 0,
			insetInlineStart: 0,
			position: 'sticky',
			zIndex: 2,
		},
		':first-of-type::after': {
			backgroundColor: wacToken('color.neutral.light400'),
			content: '""',
			insetBlock: 0,
			insetInlineEnd: 0,
			pointerEvents: 'none',
			position: 'absolute',
			width: '1px',
		},
	},
});

const V2_DIVIDER = `1px solid ${wacToken('color.neutral.light300')}`;

const dataCellV2Css = css({
	borderInlineEnd: V2_DIVIDER,
	[media.below.sm]: {
		':first-of-type': {
			borderInlineEnd: V2_DIVIDER,
			position: 'static',
			zIndex: 'auto',
		},
		':first-of-type::after': {
			content: 'none',
		},
	},
});

const rowHeaderV2Css = css({
	borderInlineEnd: V2_DIVIDER,
	':last-of-type': {
		borderInlineEnd: V2_DIVIDER,
	},
});

const rowHeaderCss = css({
	fontWeight: 700,
	textAlign: 'left',
});

// Td attributes, so a merged cell can pass colSpan/rowSpan straight through
export interface GenericTableCustomCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
	isRowHeader?: boolean;
}

export const GenericTableCustomCell = ({ isRowHeader, ...props }: GenericTableCustomCellProps) => {
	const { isV2 } = useGenericTableVariant();
	return isRowHeader ? (
		<th {...props} css={[cellCss, isV2 && rowHeaderV2Css, rowHeaderCss]} scope="row" />
	) : (
		<td {...props} css={[cellCss, isV2 && dataCellV2Css]} />
	);
};
